import React, { useState, useEffect } from 'react';
import './Experts.css';
import { getImageUrl, API_CONFIG } from '../config/apiConfig';
import { apiGet, parseJsonResponse } from '../utils/apiClient';

// Array of background colors to cycle through
const bgColors = ['#FF8A3C', '#0E9F6E', '#3F83F8', '#E3A008', '#1F2937', '#9333EA', '#EC4899', '#14B8A6'];

const Experts = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAstrologers = async () => {
      try {
        setLoading(true);
        const response = await apiGet(API_CONFIG.ENDPOINTS.GET_ASTROLOGER_LIST, { per_page: 10 });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch astrologers: ${response.status} ${response.statusText}`);
        }
        
        const result = await parseJsonResponse(response);
        
        if (result.status === 'success' && result.data && result.data.all_doctors) {
          // Map API data to component structure
          const mappedExperts = result.data.all_doctors.map((astrologer, index) => ({
            id: astrologer.id,
            name: astrologer.name,
            title: astrologer.category_name || 'Astrologer',
            image: getImageUrl(astrologer.profile_image),
            bgColor: bgColors[index % bgColors.length],
            isOnline: astrologer.is_online === 1,
            isBusy: astrologer.is_busy === 1,
            rating: astrologer.profile_rating,
            experience: astrologer.experience,
            languages: astrologer.languages || [],
            services: astrologer.expertServices || []
          }));
          
          setExperts(mappedExperts);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching astrologers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAstrologers();
  }, []);

  if (loading) {
    return (
      <section id="astrologers" className="experts-section">
        <div className="experts-container">
          <h2 className="section-title">Designed by experts, <br />delivered with care</h2>
          <p className="experts-description">Loading astrologers...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="astrologers" className="experts-section">
        <div className="experts-container">
          <h2 className="section-title">Designed by experts, <br />delivered with care</h2>
          <p className="experts-description">Error loading astrologers: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="astrologers" className="experts-section">
      <div className="experts-container">
        <h2 className="section-title">Designed by experts, <br />delivered with care</h2>
        <p className="experts-description">
          From Vedic charts to one-on-one guidance, our team of verified astrologers works together to bring you ancient wisdom.
        </p>
        <button className="learn-more-button">Learn more</button>
        
        {experts.length > 0 && (
          <div className="experts-scroll-container">
            <div className="experts-scroll">
              {experts.map((expert) => (
                <div
                  key={expert.id}
                  className="expert-card"
                  style={{ backgroundColor: expert.bgColor }}
                >
                  {expert.image ? (
                    <img 
                      src={expert.image} 
                      alt={expert.name}
                      className="expert-image"
                      onError={(e) => {
                        console.error('Image failed to load:', expert.image);
                        e.target.style.display = 'none';
                        // Show placeholder when image fails
                        const placeholder = e.target.nextElementSibling;
                        if (placeholder) {
                          placeholder.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div className="expert-image-placeholder" style={{display: expert.image ? 'none' : 'flex'}}>
                    <span>{expert.name.charAt(0)}</span>
                  </div>
                  <div className="expert-card-overlay">
                    {expert.isOnline && (
                      <span className="expert-status">Online</span>
                    )}
                    <h4 className="expert-name">{expert.name}</h4>
                    <p className="expert-title">{expert.title}</p>
                    {expert.rating && (
                      <p className="expert-rating">⭐ {expert.rating}</p>
                    )}
                  </div>
                </div>
              ))}
              {/* Duplicate cards for seamless loop */}
              {experts.map((expert) => (
                <div
                  key={`duplicate-${expert.id}`}
                  className="expert-card"
                  style={{ backgroundColor: expert.bgColor }}
                >
                  {expert.image ? (
                    <img 
                      src={expert.image} 
                      alt={expert.name}
                      className="expert-image"
                      onError={(e) => {
                        console.error('Image failed to load:', expert.image);
                        e.target.style.display = 'none';
                        // Show placeholder when image fails
                        const placeholder = e.target.nextElementSibling;
                        if (placeholder) {
                          placeholder.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div className="expert-image-placeholder" style={{display: expert.image ? 'none' : 'flex'}}>
                    <span>{expert.name.charAt(0)}</span>
                  </div>
                  <div className="expert-card-overlay">
                    {expert.isOnline && (
                      <span className="expert-status">Online</span>
                    )}
                    <h4 className="expert-name">{expert.name}</h4>
                    <p className="expert-title">{expert.title}</p>
                    {expert.rating && (
                      <p className="expert-rating">⭐ {expert.rating}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Experts;

